import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Getunits } from "../store/slices/settings";
import { Listitems } from "../store/slices/items";
import { Getsingledetail, Getirndetail } from "../store/slices/sale";
import ProductSelector from "../common/ProductSelector";
import ItemRow from "../common/ItemRow";

const UpdateInvoiceSecond = ({
  onChildDataChange,
  onSubmit,
  data,
  isViewMode,
}) => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));
  const id = user?.data?.profile_id;
  //  console.log("data",data)
  const [state, setState] = useState({
    units: [],
    itemList: [],
    singleDetail: {},
    selectedProduct: null,
    quantity: 1,
    unit_id: null,
    discount: 0,
    price: 0,
    discount_type: "Fixed",
    shippingCost: 0,
    addedItems: [],
    tax: 0,
    price_tax_type: "Excluding Tax",
    subtotal: 0,
    taxAmounts: {},
    hsn: null,
    shippingGst: 0,
    roundoff: 0,
    roundedGrandTotal: 0,
    qrcode_file: data?.invoice?.qrcode_file || null,
    irn_number: data?.invoice?.irn || null,
    acknowledge_number: data?.invoice?.ack_no || null,
    acknowledge_date: data?.invoice?.ack_date || null,
    // read shipping tax info from response (if present)
    shipping_tax_percent: Number(data?.invoice?.shipping_tax_percent) || 0,
    shipping_tax_type: data?.invoice?.shipping_tax_type || "excluding",
  });

  const fetchUnits = useCallback(async () => {
    try {
      const data = await dispatch(Getunits()).unwrap();
      setState((prevState) => ({ ...prevState, units: data.data }));
    } catch (error) {
      console.log(error.message);
    }
  }, [dispatch]);

  const fetchItemList = useCallback(async () => {
    try {
      const data = await dispatch(Listitems({ profile_id: id })).unwrap();
      setState((prevState) => ({ ...prevState, itemList: data?.data }));
    } catch (error) {
      console.error(error.message);
    }
  }, [dispatch, id]);

  const fetchGetirndetail = useCallback(async () => {
    try {
      const data = await dispatch(
        Getirndetail({
          irn: state?.irn_number,
        })
      ).unwrap();
      console.log("IRN Details:", data.data);
    } catch ({message}) {
      toast.error(message);
      console.error(message);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchUnits();
    fetchItemList();
  }, [fetchUnits, fetchItemList]);

  useEffect(() => {
    // Update qrcode/irn/ack fields and shipping tax meta
    setState((prevState) => ({
      ...prevState,
      qrcode_file: data?.invoice?.qrcode_file || null,
      irn_number: data?.invoice?.irn || null,
      acknowledge_number: data?.invoice?.ack_no || null,
      acknowledge_date: data?.invoice?.ack_date || null,
      shipping_tax_percent: Number(data?.invoice?.shipping_tax_percent) || prevState.shipping_tax_percent || 0,
      shipping_tax_type: data?.invoice?.shipping_tax_type || prevState.shipping_tax_type || "excluding",
      shippingCost: Number(data?.invoice?.shipping_cost) || prevState.shippingCost || 0,
    }));

    // Initialize addedItems from the passed data
    if (data?.items) {
      console.log("data?.items", data?.items);
      const initializedItems = data?.items?.map((item) => ({
        id: item.id,
        hsn: item.hsn,
        item_id: item.item_id,
        quantity: item.quantity,
        unit_id: item.unit_id,
        unit_name: item.unit_name,
        price: item.price,
        price_tax_type: item.price_tax_type,
        tax: item.tax,
        tax_type: item.tax_type,
        discount: item.discount,
        discount_type: item.discount_type,
        tax_amount: item.tax_amount,
        sub_total: item.sub_total,
        total_amount: item.total_amount,
      }));

      // Calculate initial taxAmounts based on data?.items
      const initialTaxAmounts = initializedItems.reduce((acc, item) => {
        const taxRate = parseFloat(item.tax) || 0;
        const taxAmount = parseFloat(item.tax_amount) || 0;

        if (taxRate > 0) {
          if (acc[taxRate]) {
            acc[taxRate] += taxAmount;
          } else {
            acc[taxRate] = taxAmount;
          }
        }
        return acc;
      }, {});

      // Shipping info from response
      const shippingCost = Number(data?.invoice?.shipping_cost) || 0;
      const shippingTaxPercent = parseFloat(data?.invoice?.shipping_tax_percent) || 0;
      const shippingTaxType = (data?.invoice?.shipping_tax_type || "excluding").toLowerCase();

      let shippingGst = 0;
      if (shippingCost > 0 && shippingTaxPercent > 0) {
        if (shippingTaxType === "including") {
          // shipping cost already includes tax, extract tax portion
          shippingGst = parseFloat(((shippingCost * shippingTaxPercent) / (100 + shippingTaxPercent)).toFixed(2));
        } else {
          // tax to be added on top of shipping
          shippingGst = parseFloat(((shippingCost * shippingTaxPercent) / 100).toFixed(2));
        }

        // Add shipping GST to initialTaxAmounts under the shipping tax percent key
        if (shippingTaxPercent > 0) {
          if (initialTaxAmounts[shippingTaxPercent]) {
            initialTaxAmounts[shippingTaxPercent] += shippingGst;
          } else {
            initialTaxAmounts[shippingTaxPercent] = shippingGst;
          }
        }
      }

      setState((prevState) => ({
        ...prevState,
        addedItems: initializedItems,
        taxAmounts: initialTaxAmounts,
        shippingCost,
        shippingGst,
        shipping_tax_percent: shippingTaxPercent,
        shipping_tax_type: shippingTaxType,
      }));
    }
  }, [data]);

  const handleProductChange = useCallback(
    async (productId) => {
      setState((prevState) => ({
        ...prevState,
        selectedProduct: productId ? productId : null,
      }));

      if (productId) {
        try {
          const data = await dispatch(
            Getsingledetail({ profile_id: id, item_id: productId })
          ).unwrap();
          setState((prevState) => ({
            ...prevState,
            price: Number(data?.data?.sale_price)?.toFixed(2),
            singleDetail: data?.data,
            unit_id: data?.data?.unit || "",
            tax: data?.data?.tax || 0,
            price_tax_type: data?.data?.sale_price_tax_type || "Excluding Tax",
            discount_type: data?.data?.discount_type || "Fixed",
            discount: Number(data?.data?.discount).toFixed(2) || 0,
          }));
        } catch (error) {
          console.error(error.message);
        }
      } else {
        // Optionally clear product details if no product is selected
        setState((prevState) => ({
          ...prevState,
          singleDetail: {},
          selectedUnit: "",
          tax: 0,
          price_tax_type: "Excluding Tax",
        }));
      }
    },
    [dispatch, id]
  );

  const handleInputChange = (field, value) => {
    if (field === "shippingCost") {
      const shippingCost = parseFloat(value) || 0;
      const shippingTaxPercent = parseFloat(state.shipping_tax_percent) || 0;
      const shippingTaxType = (state.shipping_tax_type || "excluding").toLowerCase();

      let shippingGst = 0;
      if (shippingTaxPercent > 0) {
        if (shippingTaxType === "including") {
          // shipping cost includes tax, so extract the tax portion
          shippingGst = parseFloat(((shippingCost * shippingTaxPercent) / (100 + shippingTaxPercent)).toFixed(2));
        } else {
          // tax to be added on top
          shippingGst = parseFloat(((shippingCost * shippingTaxPercent) / 100).toFixed(2));
        }
      }

      // Retrieve the existing shipping GST from the state or set it to 0 if not present
      const existingShippingGst = state.shippingGst || 0;
      const updatedTaxAmounts = { ...state.taxAmounts };
      const rateKey = shippingTaxPercent || 0;
      const difference = shippingGst - existingShippingGst;

      if (rateKey > 0) {
        if (updatedTaxAmounts[rateKey]) {
          updatedTaxAmounts[rateKey] += difference;
        } else {
          updatedTaxAmounts[rateKey] = shippingGst;
        }
      }

      // Remove the tax rate if the amount becomes zero or less
      if (rateKey > 0 && updatedTaxAmounts[rateKey] <= 0) {
        delete updatedTaxAmounts[rateKey];
      }

      // Update the state with the new shipping cost and updated taxAmounts
      setState((prevState) => ({
        ...prevState,
        shippingCost,
        shippingGst, // Store the current shipping GST for future use
        taxAmounts: updatedTaxAmounts, // Update the tax amounts including shipping GST
      }));
    } else if (field === "shipping_tax_percent" || field === "shipping_tax_type") {
      // Handle changes to shipping tax percent/type by recomputing shipping GST and updating tax buckets
      const newPercent = field === "shipping_tax_percent" ? parseFloat(value) || 0 : parseFloat(state.shipping_tax_percent) || 0;
      const newType = (field === "shipping_tax_type" ? value : state.shipping_tax_type || "excluding").toLowerCase();

      const shippingCost = parseFloat(state.shippingCost) || 0;
      let newShippingGst = 0;
      if (newPercent > 0 && shippingCost > 0) {
        if (newType === "including") {
          newShippingGst = parseFloat(((shippingCost * newPercent) / (100 + newPercent)).toFixed(2));
        } else {
          newShippingGst = parseFloat(((shippingCost * newPercent) / 100).toFixed(2));
        }
      }

      // Adjust taxAmounts: remove old shipping GST from old rate, add new to new rate
      const updatedTaxAmounts = { ...state.taxAmounts };
      const oldRate = parseFloat(state.shipping_tax_percent) || 0;
      const oldGst = parseFloat(state.shippingGst) || 0;

      if (oldRate > 0 && updatedTaxAmounts[oldRate]) {
        updatedTaxAmounts[oldRate] = parseFloat((updatedTaxAmounts[oldRate] - oldGst).toFixed(2));
        if (updatedTaxAmounts[oldRate] <= 0) delete updatedTaxAmounts[oldRate];
      }

      if (newPercent > 0) {
        if (updatedTaxAmounts[newPercent]) {
          updatedTaxAmounts[newPercent] = parseFloat((updatedTaxAmounts[newPercent] + newShippingGst).toFixed(2));
        } else {
          updatedTaxAmounts[newPercent] = newShippingGst;
        }
      }

      setState((prevState) => ({
        ...prevState,
        shipping_tax_percent: newPercent,
        shipping_tax_type: newType,
        shippingGst: newShippingGst,
        taxAmounts: updatedTaxAmounts,
      }));
    } else {
      // For other fields, update state normally
      setState((prevState) => ({ ...prevState, [field]: value }));
    }
  };

  const calculateTotal = useMemo(() => {
     const { quantity, discount, discount_type, tax, price_tax_type } = state;
    const price = Number(state.price) || 0;
    const quantityTotal = price * quantity || 0;

    const taxRate = Number(tax) || 0;
    const discountValue = Number(discount) || 0;

    let taxAmount = 0;
    let discountAmount = 0;
    let totalBeforeTax = quantityTotal;
    let preTaxTotal = quantityTotal;

    if (price_tax_type === "Including Tax") {
      // Extract pre-tax amount
      taxAmount = +((quantityTotal * taxRate) / (100 + taxRate)).toFixed(2);
      preTaxTotal = quantityTotal - taxAmount;

      // Apply discount
      if (discount_type === "Fixed") {
        discountAmount = Math.min(discountValue, quantityTotal);
      } else if (discount_type === "Percentage") {
        discountAmount = (preTaxTotal * discountValue) / 100;
      }

      // Total after discount
      totalBeforeTax =
        discountValue > 0
          ? discount_type === "Fixed"
            ? quantityTotal - discountAmount
            : preTaxTotal - discountAmount
          : quantityTotal;

      // Recalculate tax based on discounted price
      if (discountValue > 0) {
        taxAmount = +((totalBeforeTax * taxRate) / (100 + taxRate)).toFixed(2);
      }
    } else {
      // Excluding tax
      if (discount_type === "Fixed") {
        discountAmount = Math.min(discountValue, totalBeforeTax);
      } else if (discount_type === "Percentage") {
        discountAmount = (totalBeforeTax * discountValue) / 100;
      }

      totalBeforeTax -= discountAmount;
      taxAmount = +(totalBeforeTax * (taxRate / 100)).toFixed(2);
    }

    // Final total logic
    const finalTotal =
      price_tax_type === "Including Tax"
        ? +totalBeforeTax.toFixed(2) // Already includes tax
        : +(totalBeforeTax + taxAmount).toFixed(2);

    return {
      finalTotal,
      discountedTotal: totalBeforeTax,
      taxAmount,
      subtotal:
        price_tax_type === "Including Tax"
          ? +(totalBeforeTax - taxAmount).toFixed(2)
          : totalBeforeTax,
      discountAmount,
    };
 }, [state]);

  const handleAddItem = () => {
    const {
      selectedProduct,
      quantity,
      price,
      unit_id,
      singleDetail,
      price_tax_type,
      tax,
      discount,
      discount_type,
    } = state;

    if (!selectedProduct || quantity <= 0 || Number(price) <= 0) {
      toast.error("Please fill out all fields correctly.");
      return;
    }
    // Find the unit name based on the unit_id
    const unitName =
      state?.units?.find((unit) => unit.id === unit_id)?.unit || "";

    // Calculate the total for the current item
    const newItem = {
      hsn: singleDetail.hsn,
      item_id: state.selectedProduct,
      quantity: state.quantity,
      unit_id: unit_id,
      unit_name: unitName, // Use the unitName here
      price: state.price,
      price_tax_type: price_tax_type,
      tax: tax,
      tax_type: "GST",
      tax_amount: calculateTotal.taxAmount.toFixed(2),
      discount: discount,
      discount_type: discount_type,
      sub_total: calculateTotal.subtotal.toFixed(2),
      total_amount: calculateTotal.finalTotal.toFixed(2),
    };

    // Calculate the tax amount for this item
    const recalculatedTax = calculateTotal.taxAmount;

    // Copy the existing taxAmounts state
    const newTaxAmounts = { ...state.taxAmounts };

    // Update the taxAmounts state for the specific GST rate (assuming GST is stored as the tax value)
    const currentTaxRate = parseFloat(tax);
    if (currentTaxRate) {
      if (newTaxAmounts[currentTaxRate]) {
        newTaxAmounts[currentTaxRate] += recalculatedTax; // Add to existing tax amount for that rate
      } else {
        newTaxAmounts[currentTaxRate] = recalculatedTax; // Initialize new tax amount for that rate
      }
    }

    // Update the state with the new item and updated tax amounts
    setState((prevState) => ({
      ...prevState,
      addedItems: [...prevState.addedItems, newItem],
      taxAmounts: newTaxAmounts,
    }));

    clearInputs(); // Clear the form inputs for the next item
  };

  const clearInputs = () => {
    setState((prevState) => ({
      ...prevState,
      selectedProduct: null,
      quantity: 1,
      discount: 0,
      discount_type: "Fixed",
      selectedUnit: "",
      tax: 0,
      price_tax_type: "Excluding Tax",
      singleDetail: {},
      unit_id: "",
      price: 0,
    }));
  };

  const handleItemChange = useCallback(
  (field, value, index) => {
    const updatedItems = [...state.addedItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    const item = updatedItems[index];
    const itemPrice = parseFloat(item.price) || 0;
    const itemQuantity = parseFloat(item.quantity) || 0;
    const itemTotal = itemPrice * itemQuantity;

    const gstRate = parseFloat(field === "tax" ? value : item.tax) || 0;
    const discountValue = parseFloat(item.discount) || 0;
    const discountType = item.discount_type;
    const priceTaxType = item.price_tax_type;

    const calculateDiscount = (total, discountValue, type) => {
      if (type === "Percentage") return (total * discountValue) / 100;
      if (type === "Fixed") return Math.min(discountValue, total);
      return 0;
    };

    const calculateTax = (total, gst, taxIncluded) => {
      if (taxIncluded) return (total * gst) / (100 + gst);
      return (total * gst) / 100;
    };

    // Calculate discounted total
    let discountAmount = calculateDiscount(itemTotal, discountValue, discountType);
    let totalAfterDiscount = itemTotal - discountAmount;

    // Calculate tax
    let taxAmount = calculateTax(totalAfterDiscount, gstRate, priceTaxType === "Including Tax");

    // Determine final total and subtotal
    let finalTotal, subtotal;
    if (priceTaxType === "Including Tax") {
      finalTotal = totalAfterDiscount; // Already includes tax
      subtotal = totalAfterDiscount - taxAmount;
    } else {
      finalTotal = totalAfterDiscount + taxAmount; // Add GST for final total
      subtotal = totalAfterDiscount; // Pre-tax subtotal
    }

    updatedItems[index].total_amount = finalTotal.toFixed(2);
    updatedItems[index].sub_total = subtotal.toFixed(2);
    updatedItems[index].tax_amount = taxAmount.toFixed(2);
    updatedItems[index].discountAmount = discountAmount.toFixed(2);

    // Recalculate total tax amounts for all GST rates (items only)
    const updatedTaxAmounts = {};
    updatedItems.forEach((itm) => {
      const itmGst = parseFloat(itm.tax) || 0;
      if (itmGst > 0) {
        const itmTotal = parseFloat(itm.price) * parseFloat(itm.quantity) || 0;
        const itmDiscount = calculateDiscount(itmTotal, parseFloat(itm.discount) || 0, itm.discount_type);
        const itmAfterDiscount = itmTotal - itmDiscount;
        const itmTax = calculateTax(itmAfterDiscount, itmGst, itm.price_tax_type === "Including Tax");
        if (updatedTaxAmounts[itmGst]) {
          updatedTaxAmounts[itmGst] += itmTax;
        } else {
          updatedTaxAmounts[itmGst] = itmTax;
        }
      }
    });

    // Add shipping GST bucket back in so it doesn't get lost on any item edits
    const shippingRate = parseFloat(state.shipping_tax_percent) || 0;
    const shippingGst = parseFloat(state.shippingGst) || 0;
    if (shippingRate > 0 && shippingGst > 0) {
      if (updatedTaxAmounts[shippingRate]) {
        updatedTaxAmounts[shippingRate] += shippingGst;
      } else {
        updatedTaxAmounts[shippingRate] = shippingGst;
      }
    }

    // Round tax amounts to 2 decimals
    Object.keys(updatedTaxAmounts).forEach((key) => {
      updatedTaxAmounts[key] = parseFloat(updatedTaxAmounts[key].toFixed(2));
    });

    setState((prevState) => ({
      ...prevState,
      addedItems: updatedItems,
      taxAmounts: updatedTaxAmounts,
    }));
  },
    [state.addedItems, state.taxAmounts]
  );

  const handleRemoveItem = useCallback(
    (index) => {
      const updatedItems = [...state.addedItems];
      const item = updatedItems[index];

      // Recalculate the tax for the item being removed
      const gst = parseFloat(item.tax) || 0;
      const price = parseFloat(item.price) || 0;
      const itemQuantity = parseFloat(item.quantity) || 0;
      let taxAmount = 0;

      // Tax calculation based on the item's price type
      if (item.price_tax_type === "Excluding Tax") {
        taxAmount = (price * itemQuantity * gst) / 100;
      } else if (item.price_tax_type === "Including Tax") {
        const totalWithTax = price * itemQuantity;
        taxAmount = (totalWithTax * gst) / (100 + gst);
      }

      // Update tax amounts
      const newTaxAmounts = { ...state.taxAmounts };
      if (newTaxAmounts[gst]) {
        newTaxAmounts[gst] -= taxAmount;
        if (newTaxAmounts[gst] < 0) {
          newTaxAmounts[gst] = 0; // Ensure no negative tax amounts
        }
      }

      // Remove the item from addedItems
      updatedItems.splice(index, 1);

      // Update the state with the new addedItems and tax amounts
      setState((prevState) => ({
        ...prevState,
        addedItems: updatedItems,
        taxAmounts: newTaxAmounts,
      }));
    },
    [state.addedItems, state.taxAmounts]
  );

  const grandTotal = useMemo(() => {
    // Calculate total of all items and shipping
    const itemsTotal = state.addedItems.reduce(
      (sum, item) => sum + Number(item.total_amount),
      0
    );
    const shipping = Number(state.shippingCost) || 0;
    const shippingGst = Number(state.shippingGst) || 0;

    // Final grand total includes item totals + shipping + shipping GST
    return (itemsTotal + shipping + shippingGst).toFixed(2);
  }, [state.addedItems, state.shippingCost, state.shippingGst]);

  useEffect(() => {
    const discountAmount = state?.addedItems
      ?.reduce((sum, item) => {
        const discountAmountSubTotal = isNaN(Number(item.discount_amount))
          ? 0
          : Number(item.discount_amount);
        return sum + discountAmountSubTotal;
      }, 0)
      ?.toFixed(2);

    const gstTotal = state?.addedItems
      ?.reduce((sum, item) => {
        const itemSubTotal = isNaN(Number(item.tax_amount))
          ? 0
          : Number(item.tax_amount);
        return sum + itemSubTotal;
      }, 0)
      ?.toFixed(2);

    console.log("grandTotal", grandTotal);
    // Calculate the rounded grand total
    const roundedGrandTotal = Math.round(grandTotal);
    console.log("roundedGrandTotal", roundedGrandTotal);

    // Calculate round-off value
    let roundoff = 1 - (grandTotal - roundedGrandTotal);

    if (roundoff > 0.5) {
      roundoff = (1 - roundoff) * -1;
    }
    console.log("roundoff", roundoff);

    setState((prevState) => ({
      ...prevState,
      roundedGrandTotal,
      roundoff: roundoff.toFixed(2),
    }));

    const invoiceData = {
      sub_total: state.addedItems
        .reduce((sum, item) => sum + Number(item.sub_total), 0)
        .toFixed(2),
      shipping_cost: Number(state.shippingCost)?.toFixed(2),
      // include shipping tax meta so API receives selected values
      shipping_tax_percent: Number(state?.shipping_tax_percent ?? state?.shippingTaxPercent ?? 0).toFixed(2),
      shipping_tax_type: state?.shipping_tax_type ?? state?.shippingTaxType ?? null,
      shipping_tax_amount: Number(state?.shippingGst ?? state?.shippingTaxAmount ?? 0).toFixed(2),
      discount_amount: discountAmount,
      grand_total: (Number(grandTotal) + Number(roundoff)).toFixed(2),
      balance_amount: (Number(grandTotal) + Number(roundoff)).toFixed(2),
      total_gst: gstTotal,
      tcs_amount: 0.0,
      roundoff: roundoff.toFixed(2),
      invoice_items: state?.addedItems?.map((item) => ({
        id: item.id,
        item_id: item.item_id,
        quantity: item.quantity,
        unit_id: item.unit_id,
        unit_name: item.unit_name, // Use the unitName here
        price: item.price,
        price_tax_type: item.price_tax_type,
        tax_amount: item.tax_amount,
        tax: item.tax,
        tax_type: "GST",
        discount: item.discount,
        discount_type: item.discount_type,
        sub_total: item.sub_total,
        total_amount: item.total_amount,
      })),
    };
    // console.log("invoiceData",invoiceData)
    onChildDataChange(invoiceData);
  }, [state.addedItems, state.shippingCost, grandTotal, onChildDataChange]);

  // console.log('addedItems', state?.taxAmounts);

  return (
    <div className="row my-3">
      <div className="col-md-12">
        <div className="card custom-card">
          <div className="card-body">
            <table className="table item-table">
              <ProductSelector
                itemList={state?.itemList}
                selectedProduct={state?.selectedProduct}
                handleProductChange={handleProductChange}
                singleDetail={state?.singleDetail}
                handleInputChange={handleInputChange}
                state={state}
                calculateTotal={calculateTotal}
                handleAddItem={handleAddItem}
                isPurchase={false}
                isDiscount={true}
              />
              <tbody>
                <ItemRow
                  addedItems={state?.addedItems}
                  handleAddItem={handleAddItem}
                  handleInputChange={handleInputChange}
                  grandTotal={grandTotal}
                  state={state}
                  handleItemChange={handleItemChange}
                  handleRemoveItem={handleRemoveItem}
                  handleGetirndetail={fetchGetirndetail}
                  isPurchase={false}
                  isDiscount={true}
                  isSale={true}
                  isViewMode={isViewMode}
                />
              </tbody>
              {!isViewMode && (
                <tr>
                  <td colspan="10" className="text-right align-middle">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={onSubmit}
                    >
                      Update Invoice
                    </button>
                  </td>
                </tr>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateInvoiceSecond;
